import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {doc, getDoc, onSnapshot, serverTimestamp, updateDoc, writeBatch} from 'firebase/firestore';
import {db} from '../firebase-config';
import {useAuth} from '../contexts/AuthContext';
import {Alert, Box, Grid, LinearProgress, Paper, Typography, Container, CircularProgress} from '@mui/material';

// Importiere die ausgelagerten Komponenten
import QuizAnswerButton from '../components/game/QuizAnswerButton';
import QuizPlayerList from '../components/game/QuizPlayerList';
import QuizHeader from '../components/game/QuizHeader';
import GameMasterControls from '../components/game/GameMasterControls';
import PresenterView from '../components/game/PresenterView';
import '../styles/components/quiz-game.css';

const DEBUG = false; // Debug-Informationen deaktiviert

export default function QuizGame() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [routeReady, setRouteReady] = useState(false);
  const [firebaseReady, setFirebaseReady] = useState(false);

  const [gameSession, setGameSession] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGameMaster, setIsGameMaster] = useState(false);
  const [gameMasterParticipates, setGameMasterParticipates] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [allPlayersAnswered, setAllPlayersAnswered] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [animatingScores, setAnimatingScores] = useState({});
  const [isEndingQuiz, setIsEndingQuiz] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Spielzustand berechnen
  const gameState = useMemo(() => {
    const questionIdx = gameSession?.currentQuiz?.currentQuestion || 0;
    const question = quiz?.questions?.[questionIdx];
    const isFinished = !question || questionIdx >= (quiz?.questions?.length || 0);
    const progressValue = quiz?.questions ? ((questionIdx + 1) / quiz.questions.length) * 100 : 0;

    // Kompatibilitätsschicht für alte Quiz-Struktur
    const normalizedQuestion = {
      text: question?.text || question?.question, // Unterstützt beide Strukturen
      options: question?.options || question?.answers, // Unterstützt beide Strukturen
      correctAnswers: question?.correctAnswers || 
        (question?.answers ? 
          [question.answers.findIndex(a => a === question.correctAnswer)] : 
          [])
    };

    return {
      currentQuestionIdx: questionIdx,
      currentQuestion: normalizedQuestion,
      isQuizFinished: isFinished,
      progress: progressValue
    };
  }, [gameSession, quiz]);

  // Shuffle-Funktion
  const shuffleArray = useCallback((array) => {
    if (!Array.isArray(array)) {
      console.error('Versuchte ein nicht-Array zu mischen:', array);
      return [];
    }
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Firebase-Initialisierung prüfen
  useEffect(() => {
    const checkFirebase = async () => {
      try {
        if (!db) {
          console.error('Firebase db ist nicht definiert');
          setError('Firebase nicht verfügbar');
          return;
        }

        // Prüfe ob Firebase vollständig initialisiert ist
        if (!db.app?.options?.projectId) {
          console.log('Warte auf Firebase-Initialisierung...');
          return;
        }

        // Teste Firebase-Verbindung ohne doc() aufzurufen
        try {
          setFirebaseReady(true);
        } catch (err) {
          console.error('Firebase-Verbindungstest fehlgeschlagen:', err);
          setError('Firebase-Verbindung fehlgeschlagen');
        }
      } catch (err) {
        console.error('Firebase-Initialisierungsfehler:', err);
        setError('Firebase-Initialisierungsfehler');
      }
    };

    const initInterval = setInterval(checkFirebase, 100);
    
    return () => clearInterval(initInterval);
  }, []);

  // Route-Parameter prüfen
  useEffect(() => {
    if (!gameId) {
      console.log('Warte auf Route-Parameter...');
      return;
    }
    
    if (typeof gameId !== 'string' || gameId === 'undefined' || gameId.includes('/')) {
      console.error('Ungültige gameId:', gameId);
      setError('Ungültige Spiel-ID');
      return;
    }

    setRouteReady(true);
  }, [gameId]);

  // Gesamtinitialisierung
  useEffect(() => {
    if (!firebaseReady) {
      console.log('Warte auf Firebase...');
      return;
    }

    if (!routeReady) {
      console.log('Warte auf Route...');
      return;
    }

    if (!currentUser?.uid) {
      console.error('Kein Benutzer eingeloggt');
      setError('Nicht eingeloggt');
      return;
    }

    setIsInitialized(true);
  }, [firebaseReady, routeReady, currentUser]);

  // Debug-Ausgabe
  useEffect(() => {
    console.log('QuizGame mounted with gameId:', gameId);
    console.log('Current user:', currentUser?.uid);
  }, [gameId, currentUser]);

  // Quiz beenden
  const handleQuizEnd = useCallback(async () => {
    if (isEndingQuiz) return; // Verhindere mehrfache Ausführung
    setIsEndingQuiz(true);

    try {
      // Zeige die Antworten für 3 Sekunden an
      setShowAnswers(true);
      await new Promise(resolve => setTimeout(resolve, 3000));

      const batch = writeBatch(db);
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      const quizRef = doc(db, 'quizzes', quiz.id);

      // Berechne Spielerstatistiken
      const playerScores = Object.entries(gameSession.players)
        .filter(([playerId, player]) => {
          // Wenn der Spieler der Game Master ist und nicht teilnimmt, ausschließen
          if (gameSession.gameMasterId === playerId && !gameMasterParticipates) {
            return false;
          }
          return true;
        })
        .map(([playerId, player]) => ({
          playerId,
          score: player.score || 0,
          correctAnswers: player.correctAnswers || 0,
          answers: player.answers || []
        }));

      // Berechne Durchschnittswerte
      const averageScore = playerScores.length > 0 
        ? playerScores.reduce((sum, player) => sum + player.score, 0) / playerScores.length 
        : 0;
      const totalCorrectAnswers = playerScores.reduce((sum, player) => sum + player.correctAnswers, 0);
      const totalAnswers = playerScores.reduce((sum, player) => sum + player.answers.length, 0);
      const correctAnswersPercent = totalAnswers > 0 ? (totalCorrectAnswers / totalAnswers) * 100 : 0;

      // Berechne die Spielzeit
      const startTime = gameSession.currentQuiz?.startedAt?.toDate();
      const endTime = new Date();
      const completionTime = startTime ? Math.round((endTime - startTime) / 1000) : 0;

      // Erstelle scores Objekt für die Historie
      const scores = {};
      playerScores.forEach(player => {
        scores[player.playerId] = player.score;
      });

      // Aktualisiere die Quiz-Historie
      const quizHistoryEntry = {
        quizId: quiz.id,
        startedAt: gameSession.currentQuiz?.startedAt,
        endedAt: new Date().toISOString(),
        averageScore: Math.round(averageScore),
        completionTime,
        scores
      };

      // Hole bestehende Historie oder erstelle neue
      const existingHistory = gameSession.quizHistory || [];
      const updatedHistory = [...existingHistory, quizHistoryEntry];

      // Aktualisiere die Game Session
      const sessionUpdate = {
        status: 'finished',
        'currentQuiz.endedAt': serverTimestamp(),
        players: gameSession.players,
        quizHistory: updatedHistory,
        lastActivity: serverTimestamp()
      };

      // Aktualisiere die Quiz-Statistiken
      const quizDoc = await getDoc(quizRef);
      const quizData = quizDoc.data();
      const currentStats = quizData.stats || {};
      const currentMetadata = quizData.metadata || {};

      const quizUpdate = {
        'stats.totalPlays': (currentStats.totalPlays || 0) + 1,
        'stats.totalScore': (currentStats.totalScore || 0) + playerScores.reduce((sum, player) => sum + player.score, 0),
        'stats.averageScore': Math.round(averageScore),
        'stats.correctAnswersPercent': Math.round(correctAnswersPercent),
        'metadata.timesPlayed': (currentMetadata.timesPlayed || 0) + 1,
        'metadata.lastPlayed': serverTimestamp(),
        'metadata.averageCompletionTime': completionTime > 0 
          ? ((currentMetadata.averageCompletionTime || 0) * (currentMetadata.timesPlayed || 0) + completionTime) / ((currentMetadata.timesPlayed || 0) + 1)
          : currentMetadata.averageCompletionTime || 0,
        lastUpdated: serverTimestamp()
      };

      // Aktualisiere die Benutzerstatistiken
      for (const [playerId, playerData] of Object.entries(gameSession.players)) {
        // Wenn der Spieler der Game Master ist und nicht teilnimmt, überspringe ihn
        if (gameSession.gameMasterId === playerId && !gameMasterParticipates) {
          continue;
        }
        
        const userRef = doc(db, 'users', playerId);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          batch.update(userRef, {
            totalScore: (userData.totalScore || 0) + (playerData.score || 0),
            totalGamesPlayed: (userData.totalGamesPlayed || 0) + 1,
            lastPlayed: serverTimestamp(),
            lastGameId: gameId
          });
        }
      }

      // Führe alle Updates in einer Batch-Operation aus
      batch.update(gameSessionRef, sessionUpdate);
      batch.update(quizRef, quizUpdate);
      await batch.commit();

      // Navigiere zur Ergebnisseite
      navigate(`/results/${gameId}`);

    } catch (error) {
      console.error('Fehler beim Beenden des Quiz:', error);
      setError('Fehler beim Beenden des Quiz');
      setIsEndingQuiz(false); // Reset bei Fehler
    }
  }, [gameId, gameSession, quiz, navigate, isEndingQuiz, gameMasterParticipates]);

  // Quiz beenden, wenn es fertig ist
  useEffect(() => {
    if (!gameId || !gameSession || !quiz || isEndingQuiz) return;
    
    const currentQuestionIdx = gameSession?.currentQuiz?.currentQuestion || 0;
    const totalQuestions = quiz.questions?.length || 0;
    
    // Prüfe, ob alle aktiven Spieler geantwortet haben (Game Master ausschließen, wenn er nicht teilnimmt)
    const allPlayersHaveAnswered = Object.entries(gameSession.players || {}).every(([playerId, player]) => {
      // Wenn der Spieler der Game Master ist und nicht teilnimmt, überspringe ihn
      if (gameSession.gameMasterId === playerId && !gameMasterParticipates) {
        return true; // Zählt als "hat geantwortet"
      }
      return player.answers?.length >= currentQuestionIdx + 1;
    });
    
    const isQuizFinished = currentQuestionIdx >= totalQuestions - 1 && 
                          allPlayersHaveAnswered && 
                          gameSession.status === 'playing';
    
    if (isQuizFinished) {
      console.log('Quiz ist beendet - leite zur Ergebnisseite weiter...', {
        currentQuestionIdx,
        totalQuestions,
        allPlayersHaveAnswered,
        status: gameSession.status
      });
      handleQuizEnd();
    }
  }, [gameId, gameSession, quiz, handleQuizEnd, isEndingQuiz, gameMasterParticipates]);

  // Game Session laden
  useEffect(() => {
    if (!isInitialized || !gameId || !currentUser?.uid) return;

    async function loadGameAndQuiz() {
      try {
        setLoading(true);
        setError(null);

        const gameSessionRef = doc(db, 'game_sessions', gameId);
        const sessionDoc = await getDoc(gameSessionRef);
        
        if (!sessionDoc.exists()) {
          throw new Error('Spiel-Session nicht gefunden');
        }
        
        const sessionData = sessionDoc.data();
        setGameSession(sessionData);
        setIsGameMaster(sessionData.gameMasterId === currentUser.uid);

        if (sessionData.currentQuiz?.quizId) {
          const quizRef = doc(db, 'quizzes', sessionData.currentQuiz.quizId);
          const quizDoc = await getDoc(quizRef);
          if (quizDoc.exists()) {
            setQuiz({ id: quizDoc.id, ...quizDoc.data() });
          }
        }
      } catch (err) {
        console.error('Fehler beim Laden:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadGameAndQuiz();
  }, [isInitialized, gameId, currentUser?.uid]);

  // Game Session Listener für Updates
  useEffect(() => {
    if (!firebaseReady || !routeReady || !currentUser?.uid || !gameId) return;

    let unsubscribe = () => {};

    const setupListener = async () => {
      try {
        const gameRef = doc(db, 'game_sessions', gameId);
        unsubscribe = onSnapshot(gameRef, async (snapshot) => {
          if (!snapshot.exists()) {
            setError('Spiel nicht gefunden');
            return;
          }

          const gameData = snapshot.data();
          console.log("Live Update - Game Session:", {
            status: gameData.status,
            gameMasterId: gameData.gameMasterId,
            gameMasterParticipates: gameData.gameMasterParticipates,
            currentQuestion: gameData.currentQuiz?.currentQuestion,
            isPaused: gameData.isPaused
          });
          
          setGameSession(gameData);
          
          // Aktualisiere Game Master Status
          const isGM = gameData.gameMasterId === currentUser.uid;
          setIsGameMaster(isGM);
          
          // Aktualisiere Game Master Teilnahme
          if (gameData.gameMasterParticipates !== undefined) {
            console.log("Aktualisiere Game Master Teilnahme:", gameData.gameMasterParticipates);
            setGameMasterParticipates(gameData.gameMasterParticipates);
          }
          
          // Aktualisiere Pause-Status
          if (gameData.isPaused !== undefined) {
            setIsPaused(gameData.isPaused);
          }

          // Wenn das Spiel beendet ist, zur Ergebnisseite weiterleiten
          if (gameData.status === 'finished') {
            navigate(`/results/${gameId}`);
            return;
          }

          // Prüfe ob alle Spieler geantwortet haben
          const currentQuestionIdx = gameData.currentQuiz?.currentQuestion || 0;
          const allAnswered = Object.entries(gameData.players || {}).every(([playerId, player]) => {
            // Wenn der Spieler der Game Master ist und nicht teilnimmt, überspringe ihn
            if (gameData.gameMasterId === playerId && !gameData.gameMasterParticipates) {
              return true; // Zählt als "hat geantwortet"
            }
            return player.answers?.some(answer => answer.questionIndex === currentQuestionIdx);
          });

          if (allAnswered) {
            setAllPlayersAnswered(true);
            setShowAnswers(true);
          }
        });
      } catch (error) {
        console.error('Fehler beim Einrichten des Listeners:', error);
        setError('Fehler beim Laden des Spiels');
      }
    };

    setupListener();
    return () => unsubscribe();
  }, [gameId, firebaseReady, routeReady, currentUser?.uid, navigate]);

  // Timer Effect
  useEffect(() => {
    let timer;
    if (gameSession?.status === 'playing' && timeLeft > 0 && !allPlayersAnswered && !isPaused) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowAnswers(true);
            
            // Prüfe, ob wir am Ende des Quiz sind
            if (gameState.currentQuestionIdx >= (quiz?.questions?.length - 1)) {
              // Verzögere das Quiz-Ende, um die Antworten anzuzeigen
              setTimeout(() => {
                handleQuizEnd();
              }, 5000);
            }
            
            return 0;
          }
          return Math.max(0, prev - 1);
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameSession?.status, timeLeft, allPlayersAnswered, quiz?.questions?.length, gameState?.currentQuestionIdx, handleQuizEnd, isPaused]);

  // Reagiere auf Änderungen der aktuellen Frage für alle Spieler
  useEffect(() => {
    if (!gameSession?.currentQuiz?.currentQuestion) return;

    // UI für alle Spieler zurücksetzen
    setShowAnswers(false);
    setSelectedAnswer(null);
    setAllPlayersAnswered(false);
    setTimeLeft(60);
    setAnimatingScores({});
    setIsEndingQuiz(false); // Reset des End-Flags bei neuer Frage

  }, [gameSession?.currentQuiz?.currentQuestion]);

  // Antworten mischen wenn sich die Frage ändert
  useEffect(() => {
    if (!quiz?.questions) return;
    
    const currentQuestionIdx = gameSession?.currentQuiz?.currentQuestion || 0;
    const currentQuestion = quiz.questions[currentQuestionIdx];
    
    console.log('Aktuelle Frage geladen:', currentQuestion);
  }, [gameSession?.currentQuiz?.currentQuestion, quiz]);

  // Mische die Antworten für vorgefertigte Quizzes
  useEffect(() => {
    if (!quiz?.questions || !gameSession || !isGameMaster) return;
    
    const shouldShuffle = !quiz.userCreated || quiz.shuffleAnswers;
    if (!shouldShuffle) return;

    // Nur für die erste Frage und nur wenn noch keine gemischten Antworten existieren
    if (gameSession.currentQuiz?.currentQuestion === 0 && !gameSession.currentQuiz?.shuffledAnswerIndices) {
      const allShuffledIndices = quiz.questions.map(question => {
        const options = question.options || question.answers || [];
        const indices = Array.from({ length: options.length }, (_, i) => i);
        return shuffleArray(indices);
      });
      
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      updateDoc(gameSessionRef, {
        'currentQuiz.shuffledAnswerIndices': allShuffledIndices.map(indices => indices.join(','))
      }).catch(error => {
        console.error('Fehler beim Mischen der Antworten:', error);
      });
    }
  }, [quiz, gameSession, isGameMaster, gameId, shuffleArray]);

  // Antwort auswählen
  const handleAnswerSelect = async (selectedAnswerText) => {
    // Wenn der Game Master im Presenter-Modus ist, keine Antworten erlauben
    if (isGameMaster && !gameMasterParticipates) return;
    
    if (selectedAnswer !== null || !gameSession || !quiz || timeLeft <= 0) return;

    try {
      const questionIndex = gameSession.currentQuiz?.currentQuestion || 0;
      const currentQuestionData = quiz.questions[questionIndex];
      const shuffledIndices = gameSession.currentQuiz?.shuffledAnswerIndices?.[questionIndex]?.split(',').map(Number);
      
      // Bestimme die Antwortoptionen
      const originalOptions = currentQuestionData.options || currentQuestionData.answers || [];
      const currentOptions = shuffledIndices 
        ? shuffledIndices.map(i => originalOptions[i])
        : originalOptions;
      
      // Finde den Index der ausgewählten Antwort
      const selectedShuffledIndex = currentOptions.findIndex(option => option === selectedAnswerText);
      const originalIndex = shuffledIndices 
        ? shuffledIndices[selectedShuffledIndex]
        : selectedShuffledIndex;
      
      // Prüfe ob die Antwort korrekt ist
      let correctAnswer = false;
      
      if (currentQuestionData.correctAnswer) {
        // Altes Format: Direkte Textübereinstimmung
        correctAnswer = selectedAnswerText === currentQuestionData.correctAnswer;
      } else if (Array.isArray(currentQuestionData.correctAnswers)) {
        // Neues Format: Array von korrekten Indizes
        correctAnswer = currentQuestionData.correctAnswers.includes(originalIndex);
      }

      const basePoints = correctAnswer ? 200 : 0;
      const timeBonus = correctAnswer ? Math.floor((timeLeft / 60) * 800) : 0;
      const totalPoints = basePoints + timeBonus;

      const currentPlayer = gameSession.players[currentUser.uid] || {
        displayName: currentUser.displayName || 'Unbenannter Spieler',
        photoURL: currentUser.photoURL,
        score: 0,
        correctAnswers: 0,
        answers: [],
        isReady: true,
        lastActive: serverTimestamp()
      };

      const currentScore = currentPlayer.score || 0;
      const finalScore = currentScore + totalPoints;
      const correctAnswersCount = (currentPlayer.correctAnswers || 0) + (correctAnswer ? 1 : 0);

      const updatedPlayerData = {
        ...currentPlayer,
        score: finalScore,
        correctAnswers: correctAnswersCount,
        answers: [...(currentPlayer.answers || []), {
          questionIndex,
          selectedAnswer: selectedAnswerText,
          answerIndex: selectedShuffledIndex,
          isCorrect: correctAnswer,
          timeBonus,
          points: totalPoints,
          timestamp: new Date().toISOString()
        }],
        lastActive: serverTimestamp()
      };

      setSelectedAnswer(selectedAnswerText);
      
      // Punkteanimation nur bei korrekter Antwort
      if (correctAnswer && totalPoints > 0) {
        let frame = 0;
        const frames = 60;
        const animate = () => {
          if (frame < frames) {
            const progress = frame / frames;
            const easeOutProgress = 1 - Math.pow(1 - progress, 3);
            const animatedScore = Math.round(currentScore + (totalPoints * easeOutProgress));
            setAnimatingScores(prev => ({
              ...prev,
              [currentUser.uid]: animatedScore
            }));
            frame++;
            requestAnimationFrame(animate);
          } else {
            setAnimatingScores(prev => ({
              ...prev,
              [currentUser.uid]: finalScore
            }));
          }
        };
        requestAnimationFrame(animate);
      }
      
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      await updateDoc(gameSessionRef, {
        [`players.${currentUser.uid}`]: updatedPlayerData,
        lastActivity: serverTimestamp()
      });

      // Zeige die Antworten für 3 Sekunden an
      setShowAnswers(true);
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Automatisch zur nächsten Frage
      const isLastQuestion = questionIndex >= quiz.questions.length - 1;
      
      if (isLastQuestion) {
        await handleQuizEnd();
      } else {
        await updateDoc(gameSessionRef, {
          'currentQuiz.currentQuestion': questionIndex + 1,
          lastActivity: serverTimestamp()
        });
      }

    } catch (error) {
      console.error('Fehler beim Speichern der Antwort:', error);
      setError('Fehler beim Speichern der Antwort');
      setSelectedAnswer(null);
    }
  };

  // Game Master Controls für nächste Frage
  const handleNextQuestion = async () => {
    if (!gameSession || !quiz) return;
    
    const currentQuestionIdx = gameSession.currentQuiz?.currentQuestion || 0;
    const totalQuestions = quiz.questions?.length || 0;
    
    try {
      // Prüfe ob das Quiz beendet ist
      const isLastQuestion = currentQuestionIdx >= totalQuestions - 1;
      
      if (isLastQuestion) {
        console.log('Letzte Frage beantwortet - beende Quiz');
        await handleQuizEnd();
        return;
      }

      // Warte 3 Sekunden bevor die nächste Frage angezeigt wird
      setShowAnswers(true);
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Aktualisiere die Datenbank
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      await updateDoc(gameSessionRef, {
        'currentQuiz.currentQuestion': currentQuestionIdx + 1,
        lastActivity: serverTimestamp()
      });

    } catch (error) {
      console.error('Error moving to next question:', error);
      setError('Fehler beim Wechseln zur nächsten Frage');
    }
  };

  // Sortierte Spielerliste mit useMemo
  const sortedPlayers = useMemo(() => {
    if (!gameSession?.players) return [];
    
    return Object.entries(gameSession.players)
      .filter(([id, player]) => {
        // Wenn der Spieler der Game Master ist und nicht teilnimmt, ausschließen
        if (gameSession.gameMasterId === id && !gameMasterParticipates) {
          return false;
        }
        return true;
      })
      .map(([id, player]) => ({
        id,
        ...player,
        score: player.score || 0,
        correctAnswers: player.correctAnswers || 0
      }))
      .sort((a, b) => {
        // Primär nach Punkten
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // Sekundär nach korrekten Antworten
        if (b.correctAnswers !== a.correctAnswers) {
          return b.correctAnswers - a.correctAnswers;
        }
        // Tertiär nach ID für stabile Sortierung
        return a.id.localeCompare(b.id);
      });
  }, [gameSession?.players, gameSession?.gameMasterId, gameMasterParticipates]);

  // Pause-Funktion für Game Master
  const handlePauseGame = useCallback(async () => {
    if (!isGameMaster) return;
    
    try {
      console.log("Spiel pausieren");
      setIsPaused(true);
      
      // Aktualisiere den Spielstatus in der Datenbank
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      await updateDoc(gameSessionRef, {
        isPaused: true,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Fehler beim Pausieren des Spiels:', error);
    }
  }, [isGameMaster, gameId]);
  
  // Fortsetzen-Funktion für Game Master
  const handleResumeGame = useCallback(async () => {
    if (!isGameMaster) return;
    
    try {
      console.log("Spiel fortsetzen");
      setIsPaused(false);
      
      // Aktualisiere den Spielstatus in der Datenbank
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      await updateDoc(gameSessionRef, {
        isPaused: false,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Fehler beim Fortsetzen des Spiels:', error);
    }
  }, [isGameMaster, gameId]);
  
  // Zeit hinzufügen für Game Master
  const handleAddTime = useCallback(async (seconds) => {
    if (!isGameMaster) return;
    
    try {
      console.log(`${seconds} Sekunden hinzufügen`);
      setTimeLeft(prev => prev + seconds);
      
      // Aktualisiere die Zeit in der Datenbank
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      await updateDoc(gameSessionRef, {
        'currentQuiz.timeLeft': timeLeft + seconds,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Fehler beim Hinzufügen von Zeit:', error);
    }
  }, [isGameMaster, gameId, timeLeft]);
  
  // Zeit reduzieren für Game Master
  const handleReduceTime = useCallback(async (seconds) => {
    if (!isGameMaster) return;
    
    try {
      console.log(`${seconds} Sekunden reduzieren`);
      const newTime = Math.max(1, timeLeft - seconds);
      setTimeLeft(newTime);
      
      // Aktualisiere die Zeit in der Datenbank
      const gameSessionRef = doc(db, 'game_sessions', gameId);
      await updateDoc(gameSessionRef, {
        'currentQuiz.timeLeft': newTime,
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('Fehler beim Reduzieren von Zeit:', error);
    }
  }, [isGameMaster, gameId, timeLeft]);

  if (loading) return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '50vh' 
    }}>
      <LinearProgress 
        sx={{ 
          width: '100%',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, var(--color-secondary) 0%, var(--color-secondary-light) 100%)'
          }
        }}
      />
    </Box>
  );

  if (error) return (
    <Alert 
      severity="error" 
      sx={{ 
        background: 'rgba(211, 47, 47, 0.1)',
        border: '1px solid rgba(211, 47, 47, 0.3)',
        backdropFilter: 'blur(8px)'
      }}
    >
      {error}
    </Alert>
  );

  if (!quiz || !gameSession) return null;

  const isQuizFinished = !gameState.currentQuestion || gameState.currentQuestionIdx >= (quiz?.questions?.length || 0);
  const progress = quiz?.questions ? gameState.progress : 0;

  if (isQuizFinished) return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '50vh' 
    }}>
      <Typography 
        variant="h5"
        sx={{
          fontWeight: 'bold',
          background: 'linear-gradient(45deg, var(--color-secondary) 0%, var(--color-secondary-light) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
      >
        Quiz wird beendet...
      </Typography>
    </Box>
  );

  // Presenter-Ansicht für Game Master, der nicht teilnimmt
  if (isGameMaster && !gameMasterParticipates) {
    return (
      <div className="container-fixed quiz-game-container">
        <div className="quiz-game-content">
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              mb: 2,
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(90deg, var(--color-secondary) 0%, var(--color-secondary-light) 100%)'
              }
            }} 
          />
          
          <PresenterView 
            quiz={quiz}
            gameState={gameState}
            timeLeft={timeLeft}
            players={sortedPlayers}
            currentUser={currentUser}
            currentQuestionIdx={gameState.currentQuestionIdx}
            animatingScores={animatingScores}
            showAnswers={showAnswers}
            isPaused={isPaused}
          />
          
          <Box sx={{ mt: 3 }}>
            <GameMasterControls
              debug={DEBUG}
              allPlayersAnswered={allPlayersAnswered}
              timeLeft={timeLeft}
              showAnswers={showAnswers}
              onNextQuestion={handleNextQuestion}
              isLastQuestion={gameState.currentQuestionIdx >= quiz.questions.length - 1}
              onPauseGame={handlePauseGame}
              onResumeGame={handleResumeGame}
              onAddTime={handleAddTime}
              onReduceTime={handleReduceTime}
              isPaused={isPaused}
            />
          </Box>
        </div>
      </div>
    );
  }

  // Standard-Spieleransicht
  return (
    <div className="container-fixed quiz-game-container">
      <div className="quiz-game-content">
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            mb: 2,
            height: 8,
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(90deg, var(--color-secondary) 0%, var(--color-secondary-light) 100%)'
            }
          }} 
        />
        
        <Grid container spacing={3}>
          {/* Quiz-Info */}
          <Grid item xs={12}>
            <Paper 
              className="glass"
              sx={{ 
                p: 2, 
                mb: 2,
                background: 'rgba(28, 39, 60, 0.4)',
                backdropFilter: 'blur(10px)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }}
            >
              <QuizHeader
                title={quiz.title}
                currentQuestion={gameState.currentQuestionIdx + 1}
                totalQuestions={quiz.questions.length}
                timeLeft={timeLeft}
              />
            </Paper>
          </Grid>

          {/* Frage und Antworten */}
          <Grid item xs={12} md={8}>
            <Paper 
              className="glass animate-slideUp"
              sx={{ 
                p: 3,
                background: 'rgba(28, 39, 60, 0.4)',
                backdropFilter: 'blur(10px)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }}
            >
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  color: 'var(--color-text-primary)',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {gameState.currentQuestion.text}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {(() => {
                  const questionIndex = gameSession.currentQuiz?.currentQuestion || 0;
                  const currentQuestionData = quiz.questions[questionIndex];
                  const shuffledIndices = gameSession.currentQuiz?.shuffledAnswerIndices?.[questionIndex]?.split(',').map(Number);
                  const originalOptions = currentQuestionData.options || currentQuestionData.answers || [];
                  const displayOptions = shuffledIndices 
                    ? shuffledIndices.map(i => originalOptions[i])
                    : originalOptions;

                  return displayOptions.map((option, index) => {
                    const originalIndex = shuffledIndices 
                      ? shuffledIndices[index]
                      : index;

                    // Bestimme die korrekte Antwort basierend auf dem Format
                    let isCorrect = false;
                    if (currentQuestionData.correctAnswer) {
                      // Altes Format: Direkte Textübereinstimmung
                      isCorrect = option === currentQuestionData.correctAnswer;
                    } else if (Array.isArray(currentQuestionData.correctAnswers)) {
                      // Neues Format: Array von korrekten Indizes
                      isCorrect = currentQuestionData.correctAnswers.includes(originalIndex);
                    }

                    return (
                      <QuizAnswerButton
                        key={option}
                        answer={option}
                        isSelected={selectedAnswer === option}
                        isCorrect={isCorrect}
                        hasAnswered={selectedAnswer !== null}
                        showResult={showAnswers || allPlayersAnswered || timeLeft <= 0}
                        onSelect={() => handleAnswerSelect(option)}
                        disabled={timeLeft <= 0 || selectedAnswer !== null}
                      />
                    );
                  });
                })()}
              </Grid>
            </Paper>
          </Grid>

          {/* Spieler-Status */}
          <Grid item xs={12} md={4}>
            <Paper 
              className="glass"
              sx={{ 
                p: 2,
                background: 'rgba(28, 39, 60, 0.4)',
                backdropFilter: 'blur(10px)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }}
            >
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  color: 'var(--color-secondary)'
                }}
              >
                Spieler
              </Typography>
              <QuizPlayerList
                players={sortedPlayers}
                currentUser={currentUser}
                currentQuestionIdx={gameState.currentQuestionIdx}
                animatingScores={animatingScores}
              />
            </Paper>

            {/* Game Master Controls */}
            {isGameMaster && (
              <GameMasterControls
                debug={DEBUG}
                allPlayersAnswered={allPlayersAnswered}
                timeLeft={timeLeft}
                showAnswers={showAnswers}
                onNextQuestion={handleNextQuestion}
                isLastQuestion={gameState.currentQuestionIdx >= quiz.questions.length - 1}
                onPauseGame={handlePauseGame}
                onResumeGame={handleResumeGame}
                onAddTime={handleAddTime}
                onReduceTime={handleReduceTime}
                isPaused={isPaused}
              />
            )}
          </Grid>
        </Grid>
      </div>
    </div>
  );
} 